import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import React from 'react'

import { useGetNotificationItemsQuery } from 'api/notification-center/notificationCenter'
import T from 'T'
import {
  NOTIFICATION_ENTITY_TYPE,
  NOTIFICATION_ITEM_OUTCOME,
  isUnread,
  type Notification,
  type NotificationEntityType,
} from 'types/modules/notification-center/notification'

import {
  CARD_BORDER_RADIUS,
  DETAIL_PANEL_PADDING_X,
  DETAIL_PANEL_PADDING_Y,
  META_LABEL_WIDTH,
  UNREAD_RAIL_WIDTH,
} from './notificationCenterConstants'
import { bodyFor, CATEGORY_LABELS, customerLabelFor, progressFor, severityOf } from './notificationCenterViews'
import NotificationEntityLink from './NotificationEntityLink'
import { useOptimizationReview } from './useOptimizationReview'

const ENTITY_TYPE_LABELS: Readonly<Record<NotificationEntityType, string>> = Object.freeze({
  [NOTIFICATION_ENTITY_TYPE.ACCOUNT]: T.CUSTOMER,
  [NOTIFICATION_ENTITY_TYPE.WORK_ORDER]: T.WORK_ORDER,
  [NOTIFICATION_ENTITY_TYPE.INVOICE]: T.INVOICE,
  [NOTIFICATION_ENTITY_TYPE.ROUTE]: T.ROUTE,
  [NOTIFICATION_ENTITY_TYPE.LOCATION]: T.LOCATION,
})

type MetaRowProps = {
  label: string
  children: React.ReactNode
}

const MetaRow = ({ label, children }: MetaRowProps) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
    <Typography color="text.secondary" component="p" sx={{ width: META_LABEL_WIDTH, flexShrink: 0, fontWeight: 400 }} variant="h6">
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
)

type NotificationDetailPanelProps = {
  notification: Notification
}

/**
 * Expanded region of a notification, in the prototype's order: metadata, long-form
 * details, progress, then the per-item drill-down with reason chips. Read state is
 * the card's job — the dot, the row button and the context menu all set it.
 */
const NotificationDetailPanel = ({ notification }: NotificationDetailPanelProps) => {
  const theme = useTheme()
  const { data, isLoading } = useGetNotificationItemsQuery({ id: notification.id }, { skip: !notification.hasItems })

  const unread = isUnread(notification)
  const severityColor = theme.palette[severityOf(notification)].main
  const items = data?.items ?? []
  const totalItems = data?.totalItems ?? items.length
  const isTruncated = data?.truncated ?? false
  const percentComplete = progressFor(notification)
  const customerLabel = customerLabelFor(notification)
  const body = bodyFor(notification)
  const { optionsUsed } = useOptimizationReview(notification)
  const entityLabel = notification.entityType ? ENTITY_TYPE_LABELS[notification.entityType] : T.CUSTOMER

  // Continues the card's frame: same rail, same sides, rounded only at the bottom.
  const panelSx = {
    px: DETAIL_PANEL_PADDING_X,
    py: DETAIL_PANEL_PADDING_Y,
    mb: 0.75,
    border: 1,
    borderTop: 'none',
    borderColor: 'divider',
    borderLeftWidth: UNREAD_RAIL_WIDTH,
    borderLeftColor: unread ? severityColor : 'divider',
    borderBottomLeftRadius: theme.spacing(CARD_BORDER_RADIUS),
    borderBottomRightRadius: theme.spacing(CARD_BORDER_RADIUS),
    bgcolor: 'background.default',
  }

  return (
    <Box sx={panelSx}>
      {/* The sentence the closed card no longer carries: what happened, before the fields that qualify it. */}
      {body && (
        <Typography component="p" sx={{ fontWeight: 400, mb: 1 }} variant="h6">
          {body}
        </Typography>
      )}
      {optionsUsed && (
        <Typography color="text.secondary" component="p" sx={{ fontWeight: 400, mb: 1.5 }} variant="subtitle2">
          {optionsUsed}
        </Typography>
      )}
      <MetaRow label={T.RECEIVED}>
        <Typography component="p" sx={{ fontWeight: 400 }} variant="h6">
          {format(new Date(notification.occurredAt), 'MM/dd/yyyy h:mm a')}
        </Typography>
      </MetaRow>
      <MetaRow label={T.CATEGORY}>
        <Typography component="p" sx={{ fontWeight: 400 }} variant="h6">
          {CATEGORY_LABELS[notification.category]}
        </Typography>
      </MetaRow>
      {customerLabel && (
        <MetaRow label={entityLabel}>
          <NotificationEntityLink
            entityId={notification.entityId}
            entityType={notification.entityType}
            jobId={notification.payload?.jobId}
            label={customerLabel}
            serviceDate={notification.payload?.serviceDate}
            sx={{ fontWeight: 400 }}
            variant="h6"
          />
        </MetaRow>
      )}
      <MetaRow label={T.STATUS}>
        <Typography color={unread ? 'primary.main' : 'text.secondary'} component="p" sx={{ fontWeight: 400 }} variant="h6">
          {unread ? T.UNREAD : T.READ}
        </Typography>
      </MetaRow>

      {notification.payload?.details && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography color="text.secondary" variant="subtitle2">
            {T.DETAILS}
          </Typography>
          <Typography color="text.primary" component="p" sx={{ whiteSpace: 'pre-line', mt: 0.5, fontWeight: 400 }} variant="h6">
            {notification.payload.details}
          </Typography>
        </Box>
      )}

      {percentComplete !== undefined && notification.totalCount ? (
        <Typography color="text.secondary" component="p" sx={{ mt: 1, fontWeight: 400 }} variant="h6">
          {`${(notification.succeededCount ?? 0).toLocaleString('en-US')} ${T.OF} ${notification.totalCount.toLocaleString('en-US')} ${
            T.PROCESSED_LOWER
          }`}
        </Typography>
      ) : null}

      {isLoading && <Skeleton height={80} sx={{ mt: 2 }} variant="rounded" />}

      {!isLoading && items.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography color="text.secondary" variant="subtitle2">
            {`${totalItems} ${T.AFFECTED} ${T.ITEMS}`}
          </Typography>
          <Stack
            divider={<Divider />}
            sx={{ mt: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', px: 2 }}
          >
            {items.map((item, index) => (
              <Stack
                // Both ref fields are nullable, so ref-less rows would all collide on one key and the
                // contract exposes no item id to use instead.
                // eslint-disable-next-line react/no-array-index-key
                key={`${item.refType}-${item.refId}-${index}`}
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 1.25 }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <NotificationEntityLink
                    entityId={item.refId}
                    entityType={item.refType}
                    label={item.refLabel ?? item.refId ?? T.UNKNOWN}
                    sx={{ fontWeight: 500 }}
                    variant="h6"
                  />
                  {item.refId && item.refId !== item.refLabel && (
                    <Typography color="text.secondary" sx={{ fontWeight: 400 }} variant="subtitle2">
                      {item.refId}
                    </Typography>
                  )}
                </Stack>
                <Chip
                  color={item.outcome === NOTIFICATION_ITEM_OUTCOME.FAILED ? 'error' : 'warning'}
                  label={item.reasonMessage ?? item.reasonCode ?? item.outcome}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            ))}
          </Stack>
          {isTruncated && (
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
              {T.NOTIFICATION_ITEMS_TRUNCATED}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default NotificationDetailPanel
